import { useState } from 'react';
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  GitCompare,
  Loader2,
  Play,
  Save,
  Building2,
} from 'lucide-react';

import ApprovalDesignerCanvas from './ApprovalDesignerCanvas';
import BpmRuleFlowPreview from './BpmRuleFlowPreview';
import BpmSimulatePanel from './BpmSimulatePanel';
import { resolveSourceBadgeLabel } from './BpmTaskConfigOverview';

function Section({ title, count, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between border-b border-slate-100 px-3 py-2 text-left"
      >
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
          {title}
          {count != null ? ` (${count})` : ''}
        </h4>
        {open ? <ChevronDown className="h-3.5 w-3.5 text-slate-400" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400" />}
      </button>
      {open && <div className="max-h-48 overflow-y-auto p-2 text-xs text-slate-700">{children}</div>}
    </section>
  );
}

export default function BpmTaskDetailPanel({
  task,
  tenantSlice,
  taskConfiguration,
  configLoading,
  configError,
  endpointProbe,
  design,
  onDesignChange,
  transactionCategory = 'absence',
  api,
  draftMongoId,
  onSaveDraft,
  saveState = 'idle',
  onRunRoutingSimulate,
  routingSimulateLoading,
  routingSimulateResult,
  onCompare,
  compareLoading,
  compareResult,
}) {
  const [showEdit, setShowEdit] = useState(false);

  if (!task) {
    return (
      <div className="flex h-full min-h-[200px] flex-col items-center justify-center p-6 text-center text-sm text-slate-500">
        <Building2 className="mb-2 h-8 w-8 text-slate-300" aria-hidden />
        <p>Select an approval task from the catalog or configured list.</p>
      </div>
    );
  }

  const sourceBadge =
    taskConfiguration?.sourceBadge === 'sql'
      ? 'sql'
      : taskConfiguration?.tenantSql || taskConfiguration?.sqlMatchRow
        ? 'sql'
        : 'catalog';
  const badge = resolveSourceBadgeLabel(sourceBadge);
  const displayMode = taskConfiguration?.displayMode || 'sql_fusion_reference';
  const fusionLinks = taskConfiguration?.fusionLinks || taskConfiguration?.fusionUrls;
  const showConfigError = configError && !taskConfiguration && !configLoading;
  const showPodRestProbe = process.env.REACT_APP_SHOW_POD_REST_PROBE === 'true';

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <header className="shrink-0 border-b border-slate-100 px-4 py-2">
        <div className="flex flex-wrap items-start gap-2">
          <h3 className="min-w-0 flex-1 text-sm font-semibold text-slate-900">{task.name}</h3>
          <span className={`shrink-0 rounded px-2 py-0.5 text-[10px] font-medium ${badge.className}`}>
            {badge.text}
          </span>
        </div>
      </header>

      {showConfigError && (
        <div className="shrink-0 mx-4 mt-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-900">
          {configError}
        </div>
      )}

      <BpmRuleFlowPreview
        taskKey={task.taskKey || taskConfiguration?.taskKey || 'global-absence-recording-single-assignment'}
        taskQuery={{
          name: task.name,
          code: task.code,
          category: task.category || transactionCategory,
        }}
      />

      <div className="min-h-0 flex-1 overflow-y-auto border-t border-slate-200 bg-slate-50/50 p-3 space-y-2">
        <Section title="Fusion Transaction Console" defaultOpen>
          <p className="mb-2 text-[11px] text-slate-600">
            {fusionLinks?.navigatorPath || 'Navigator → Tools → Transaction Console'}
          </p>
          <div className="flex flex-wrap gap-2">
            {(fusionLinks?.links || []).map((link) => (
              <a
                key={link.id}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg bg-cyan-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-cyan-800"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                {link.label}
              </a>
            ))}
          </div>
        </Section>

        <section className="rounded-lg border border-cyan-200 bg-cyan-50/30">
          <div className="flex items-center justify-between gap-2 border-b border-cyan-100 px-3 py-2">
            <h4 className="text-[11px] font-semibold uppercase tracking-wide text-cyan-900">Draft design</h4>
            <button
              type="button"
              onClick={() => setShowEdit((v) => !v)}
              className="text-[10px] font-medium text-cyan-800 hover:underline"
            >
              {showEdit ? 'Collapse' : 'Expand'} canvas
            </button>
          </div>
          <div className="space-y-2 p-3">
            {showEdit && design?.stages?.length > 0 && (
              <div className="h-[min(240px,30vh)] min-h-[180px] rounded-md border border-slate-200 bg-white overflow-hidden">
                <ApprovalDesignerCanvas design={design} onDesignChange={onDesignChange} readOnly={false} />
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={onSaveDraft}
                disabled={!design?.stages?.length || saveState === 'saving'}
                title="Saves draft to Mongo only — no Oracle push"
                className="inline-flex items-center gap-1.5 rounded-lg bg-cyan-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-cyan-800 disabled:bg-slate-300"
              >
                {saveState === 'saving' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                Save draft
              </button>
              <button
                type="button"
                onClick={onRunRoutingSimulate}
                disabled={!design?.stages?.length || routingSimulateLoading}
                className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-medium text-emerald-900 hover:bg-emerald-50 disabled:opacity-50"
              >
                {routingSimulateLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                Simulate
              </button>
              <button
                type="button"
                onClick={onCompare}
                disabled={!draftMongoId || compareLoading}
                className="inline-flex items-center gap-1.5 rounded-lg border border-violet-300 bg-white px-3 py-1.5 text-xs font-medium text-violet-900 hover:bg-violet-50 disabled:opacity-50"
              >
                {compareLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <GitCompare className="h-3.5 w-3.5" />}
                Compare
              </button>
            </div>
            {routingSimulateResult?.summary && (
              <p className="text-[10px] text-emerald-800">{routingSimulateResult.summary}</p>
            )}
            {compareResult?.summary && <p className="text-[10px] text-violet-800">{compareResult.summary}</p>}
          </div>
        </section>

        {displayMode === 'rest_rules' && (
          <Section title="Runtime BPM tasks" count={tenantSlice?.bpmTasks?.length || 0}>
            {(tenantSlice?.bpmTasks || []).length === 0 ? (
              <p className="text-slate-500">No matching BPM tasks.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {(tenantSlice.bpmTasks || []).slice(0, 10).map((t, i) => (
                  <li key={i} className="py-1">
                    {t.taskDefinitionName} · {t.state}
                  </li>
                ))}
              </ul>
            )}
          </Section>
        )}

        {showPodRestProbe && (
          <Section title="Pod REST probe" count={endpointProbe?.length || 0} defaultOpen={false}>
            <ul className="text-[10px] font-mono text-slate-600">
              {(endpointProbe || []).slice(0, 6).map((e) => (
                <li key={e.path}>
                  {e.path} — {e.status} ({e.itemCount ?? 0})
                </li>
              ))}
            </ul>
          </Section>
        )}

        {design?.stages?.length > 0 && api && (
          <BpmSimulatePanel design={design} api={api} disabled={false} />
        )}

        {!api && (
          <div className="flex items-start gap-2 rounded-lg bg-rose-50 p-2 text-xs text-rose-800">
            <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>Draft API unavailable — sign in and ensure MongoDB is configured.</span>
          </div>
        )}
      </div>
    </div>
  );
}
